import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Plus, X, ArrowLeft } from "lucide-react";
import { istToday } from "@/lib/dateFormat";

export default function SSOpeningStockPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const ssId = searchParams.get("ss_id") || "";
  const ssName = searchParams.get("ss_name") || "";

  const [effectiveDate, setEffectiveDate] = useState(istToday());
  const [skuLines, setSkuLines] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [skus, setSkus] = useState([]);

  useEffect(() => {
    if (!ssId) return;
    api.get(`/ss-inventory/stock/${ssId}`).then(r => {
      const s = r.data || [];
      setSkus(s);
      const lines = {};
      s.forEach(sk => { lines[sk.sku_id] = [{ batch_number: "", qty: 0, notes: "" }]; });
      setSkuLines(lines);
    }).catch(() => {});
  }, [ssId]);

  const addBatch = (skuId) => {
    setSkuLines(prev => ({ ...prev, [skuId]: [...(prev[skuId] || []), { batch_number: "", qty: 0, notes: "" }] }));
  };

  const removeBatch = (skuId, idx) => {
    setSkuLines(prev => ({ ...prev, [skuId]: prev[skuId].filter((_, i) => i !== idx) }));
  };

  const updateBatch = (skuId, idx, field, value) => {
    setSkuLines(prev => {
      const lines = [...(prev[skuId] || [])];
      lines[idx] = { ...lines[idx], [field]: field === "batch_number" ? value.toUpperCase().replace(/[^A-Z0-9]/g, "") : value };
      return { ...prev, [skuId]: lines };
    });
  };

  const handleSubmit = async () => {
    const lines = [];
    Object.entries(skuLines).forEach(([skuId, batches]) => {
      const sku = skus.find(s => s.sku_id === skuId);
      batches.forEach(b => {
        if (b.qty > 0) lines.push({ sku_id: skuId, sku_name: sku?.sku_name || skuId, qty: b.qty, batch_number: b.batch_number, notes: b.notes });
      });
    });
    if (!lines.length) { toast.error("Enter qty for at least one SKU"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/ss-inventory/opening-stock", { ss_customer_id: ssId, effective_date: effectiveDate, lines });
      toast.success(data.message || "Opening stock recorded");
      navigate("/ss-inventory");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setSubmitting(false);
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/ss-inventory")} className="text-zinc-400 hover:text-zinc-600"><ArrowLeft size={18} /></button>
        <div><h1 className="font-heading text-xl font-bold text-zinc-900">Add Opening Stock</h1><p className="text-sm text-zinc-500">{ssName}</p></div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm p-4">
        <Label className="text-xs">Effective Date</Label>
        <Input type="date" value={effectiveDate} onChange={e => setEffectiveDate(e.target.value)} className="h-8 text-xs w-48 mt-1" />
      </div>

      {skus.map(sku => {
        const batches = skuLines[sku.sku_id] || [{ batch_number: "", qty: 0, notes: "" }];
        const total = batches.reduce((s, b) => s + (parseInt(b.qty) || 0), 0);
        return (
          <div key={sku.sku_id} className="bg-white border border-zinc-200 rounded-sm p-4">
            <div className="flex justify-between items-center mb-3">
              <div><span className="text-sm font-semibold text-zinc-800">{sku.sku_name}</span><span className="text-[10px] text-zinc-400 ml-2">{sku.sku_id}</span></div>
              <span className="text-xs font-mono text-zinc-500">Total Qty: <strong className="text-zinc-800">{total}</strong></span>
            </div>
            {batches.map((b, i) => (
              <div key={i} className="flex items-center gap-2 mb-2">
                <Input value={b.batch_number} onChange={e => updateBatch(sku.sku_id, i, "batch_number", e.target.value)} placeholder="Batch #" className="h-7 text-xs w-32" />
                <Input type="number" value={b.qty || ""} onChange={e => updateBatch(sku.sku_id, i, "qty", parseInt(e.target.value) || 0)} placeholder="Qty" className="h-7 text-xs w-24" />
                <Input value={b.notes || ""} onChange={e => updateBatch(sku.sku_id, i, "notes", e.target.value)} placeholder="Notes (optional)" className="h-7 text-xs flex-1" />
                {i > 0 && <button onClick={() => removeBatch(sku.sku_id, i)} className="text-red-400 hover:text-red-600"><X size={14} /></button>}
              </div>
            ))}
            <button onClick={() => addBatch(sku.sku_id)} className="text-[10px] text-blue-600 hover:text-blue-800 mt-1"><Plus size={10} className="inline" /> Add Batch</button>
          </div>
        );
      })}

      <div className="flex gap-3">
        <Button onClick={handleSubmit} disabled={submitting} className="rounded-sm text-xs">{submitting ? "Saving..." : "Record Opening Stock"}</Button>
        <Button variant="outline" onClick={() => navigate("/ss-inventory")} className="rounded-sm text-xs">Cancel</Button>
      </div>
    </div>
  );
}
